import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { LayoutDashboard, FlaskConical, Trophy, MessageCircle, User } from "lucide-react";

const navItems = [
  { path: "/dashboard", label: "Home", icon: LayoutDashboard },
  { path: "/learning", label: "Subjects", icon: FlaskConical },
  { path: "/leaderboard", label: "Leaders", icon: Trophy },
  { path: "/messages", label: "Messages", icon: MessageCircle },
  { path: "/profile", label: "Profile", icon: User },
];

export function MobileBottomNav() {
  const { user, isAuthenticated } = useAuth();
  const [location, navigate] = useLocation();
  const search = useSearch();

  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ['/api/messages/unread-count'],
    enabled: isAuthenticated && !!user,
    refetchInterval: 30000,
  });
  const unreadCount = unreadData?.count ?? 0;

  if (!isAuthenticated || !user) {
    return null;
  }

  const isQuizMode = location === '/learning' && new URLSearchParams(search).get('mode') === 'quiz';
  if (isQuizMode) {
    return null;
  }

  // The lesson player has its own sticky footer; hide the tab bar so it does not overlap.
  if (/^\/lessons\/[^/]+/.test(location)) {
    return null;
  }

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden border-t bg-background"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="flex items-center justify-around gap-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location === item.path;
          const isMessages = item.path === "/messages";
          const showBadge = isMessages && unreadCount > 0;

          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center flex-1 min-h-[44px] py-2 gap-0.5 ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`}
            >
              <div className="relative">
                <Icon className="h-5 w-5" />
                {showBadge && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-2 -right-3 h-4 min-w-[16px] flex items-center justify-center p-0 text-[10px]"
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </Badge>
                )}
              </div>
              <span className="text-xs leading-tight">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
